import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Transformation Management — Halil's page 3. Transformation Narrative, TMO Setup
 * and TMO Advisory, one flowing page.
 *
 * Two structural edits, both recorded in `docs/website/COPY-REVIEW.md`:
 *
 *  * **"Do you need a Chief Transformation Officer?" is not here.** It is 200 words
 *    answering a question the reader has not asked yet, sitting between the TMO
 *    setup and the TMO cadence — the two things somebody on this page is trying to
 *    compare. It is a strong article and a poor mid-page detour; it belongs in the
 *    blog, where the CIO transformation series already lives.
 *  * **No "How we work" section.** It appears on all three solution pages in the
 *    document and goes flat by the third reading, so it stays on Strategy
 *    Consulting and the Navigator Platform is named in one line here instead.
 *  * **No closing `cta` band.** Removed on request (2026-08-28); the page now ends
 *    on TMO Advisory, whose "Book a Call" carries the same ask. The `check` note
 *    about the missing customer evidence moved onto that section so it stays open.
 */
export const transformationManagement: WrittenPage = {
  navLabel: 'Transformation Management',
  title: 'Transformation Management Office (TMO)',
  description:
    'Transformation narrative, TMO setup and TMO advisory. Turn a portfolio of intentions into governed execution with value you can see.',
  sections: [
    {
      kind: 'hero',
      headline:'Transformation rarely fails from lack of ambition. It fails from fragmentation.',
      sub: 'Assure and accelerate your agentic AI journey. We build the narrative, stand up the office, and stay for the cadence.',
      photo: {
        src: '/photo/factory-roadmap.jpg',
        alt: 'A Digitopia consultant presenting a transformation roadmap, covering growth, operational efficiency and technology programs on a quarterly timeline, to a team on a factory floor.',
      },
      // `#contact` rather than the Contact Us page: the closing form sits at the
      // bottom of this document (`components/ContactBand.tsx`), so Book a Call
      // scrolls the reader down to it instead of sending them a click away.
      ctas: [{ label: 'Book a Call', href: '#contact' }],
    },
    {
      kind: 'list',
      heading: 'You have seen this shape before.',
      variant: 'steps',
      items: [
        'The strategy says one thing.',
        'The budget says another.',
        'The operating model resists both.',
        'The people are overloaded.',
        'Governance is either too weak or too slow.',
        'And the change portfolio becomes a cemetery of half-finished intentions.',
      ],
      body: 'AI did not create the need for transformation. It increased its speed, scope, interdependence and consequences. What used to be a technology program is now an enterprise challenge, and strategy, governance, operating model, data, risk, talent and culture all have to move together.',
    },
    {
      kind: 'list',
      heading: 'Transformation Narrative.\nName it, or nobody follows it.',
      intro:
        'Most transformations have a deck and no story. A narrative gives the change a name people recognize, a target state instead of a vague ambition, an accountable champion, and a reason that survives contact with the day job.',
      variant: 'check',
      items: [
        'Does your transformation have a name people recognize?',
        'Does it have a clear target state, not just an ambition?',
        'Does it have an accountable executive champion?',
        'Do you have a guiding coalition across business, technology, finance, HR and risk?',
        'Are your priorities explicit and ruthless enough to stop low-value work?',
        'Do you know how much change your organization can absorb, without burning out your people?',
      ],
      body: 'If several answers are "not really", "sort of" or "it depends", the problem is not execution. The problem is transformation design.',
      cta: { label: 'Transformation Readiness Check', href: LINKS.appetiteTool },
      // Points at the Transformation Readiness Diagnostic since 2026-08-26, and
      // the CTA was renamed from "Take the Transformation Appetite Tool" to
      // "Transformation Readiness Check" on request — the label now reads as the
      // readiness page it opens rather than as a second, differently named tool.
    },
    {
      kind: 'features',
      heading: 'TMO Setup. The engine room.',
      sub: 'A real TMO is not a PMO with a new label, and it is not a reporting factory. It is small, credible, cross-functional and close to power.',
      columns: 3,
      // Six short items set at the default size read as small print beside the
      // heading above them; `textSize: 'lg'` is the renderer's own step up
      // (item titles to `text-xl`, bodies to `text-lg`).
      textSize: 'lg',
      items: [
        { title: 'Steer the portfolio', body: 'One view of every transformation initiative, and the authority to sequence them.' },
        { title: 'Enable executive decisions', body: 'Decisions arrive framed, with the trade-off already made explicit.' },
        { title: 'Track progress and risk', body: 'Performance and KPI tracking, dependencies and escalation.' },
        { title: 'Integrate change management', body: 'Communications and adoption inside execution, not bolted on after it.' },
        { title: 'Protect focus and capacity', body: 'The organization can only absorb so much. Someone has to hold that line.' },
        { title: 'Support value realization', body: 'Finance alongside delivery, so benefits are tracked rather than assumed.' },
      ],
      cta: { label: 'Book a Call', href: '#contact' },
    },
    {
      // Between the office being built and the cadence it runs: the small,
      // cross-functional team the section above describes, in the room together.
      kind: 'photo',
      photo: {
        src: '/photo/team-high-five-office.png',
        alt: 'Four colleagues meeting around a small table in a bright office, hands meeting in a high five over an open laptop and notes.',
      },
    },
    {
      kind: 'list',
      heading: 'TMO Advisory. Rhythm is the product.',
      intro:
        'We sit alongside your transformation leadership, chair the hard reviews when that helps, and escalate when it is uncomfortable. Governance is not bureaucracy here. It is system design. The Navigator Platform runs the machinery underneath: dashboards, dependencies, benefits tracking and the review pack, assembled before the meeting rather than during it.',
      items: [
        'Quarterly business reviews',
        'Monthly steering reviews',
        'Initiative level dashboards',
        'Dependency and risk escalation',
        'Benefits tracking',
        'Decisions captured and followed through',
      ],
      cta: { label: 'Book a Call', href: '#contact' },
      check:
        'This page ships with NO customer evidence, and it is the only solution page ' +
        'without any. Two proof points are written and withheld pending permission: TOGG ' +
        '(annual reassessment plus quarterly reviews made progress visible and ' +
        'strategically actionable, alongside close to 10% operational efficiency ' +
        'improvement) and Sonexus (AIMI exposed governance and capability gaps and turned ' +
        'them into governance changes, enablement actions and project selection inside 90 ' +
        'days). Confirm both for public naming, and confirm the 10% figure can be ' +
        'published — it currently comes from internal material. This is the biggest gap ' +
        'on the site.',
    },
    {
      // Strategy Consulting's Success Stories section, same design and the same
      // proportions, carried here on 2026-08-31 (*"tasarımın uygun olduğu
      // koyulabilecek örnekler varsa aynı profesyonel tasarımı ve oranları
      // kompozisyonu bozmadan öbür solutions sayfalarında da olabilir"*).
      //
      // The three tabs are chosen for *this* page's subject — cadence and
      // governance rather than the assessment itself: Borusan on evaluating
      // every three and six months, Petrol Ofisi on quarterly measurement and
      // tailored roadmaps, ebebek on periodic evaluation keeping them on track.
      // Every word, mark and portrait is the one `content/testimonials/`
      // already carries, so nothing new is claimed and no page can drift.
      //
      // It sits after TMO Advisory and before the closing form, which is where
      // the reader has just been told what the cadence is and can now hear
      // three customers describe living inside one.
      kind: 'quotes',
      variant: 'minimal-carousel',
      heading: 'Success Stories',
      items: [
        {
          company: 'Borusan Otomotiv',
          logo: { name: 'Borusan Otomotiv', src: '/logo/borusanotomotivcard.png' },
          portrait: { src: '/photo/portrait-asli-de-munnik.png', alt: 'Aslı de Munnink' },
          quote: [
            'Digital maturity is not a job that should be evaluated once a year and then put '
            + 'on the shelves to collect dust. Evaluating our actions every three months and '
            + 'six months allows us to see our progress and motivates us even more for our '
            + 'long-term goals.',
          ],
          source: 'Aslı de Munnink, CDO',
          cta: { label: 'Read more', href: '/en/customers/borusan-otomotiv-grup' },
        },
        {
          company: 'Petrol Ofisi',
          logo: { name: 'Petrol Ofisi', src: '/logo/petrolofisicard.png' },
          portrait: { src: '/photo/portrait-mehmet-ali-gulsen.png', alt: 'Mehmet Ali Gülşen' },
          quote: [
            'We collaborate with Digitopia because they are best-in-class and bring a '
            + 'customer-centric approach to our partnership. Their continuous support, such as '
            + 'measuring our progress every three months and crafting tailored digital '
            + 'roadmaps, has been invaluable.',
            'By engaging our employees in the digitalization journey and fostering their '
            + 'active involvement, Digitopia has helped us lay a strong foundation for '
            + 'transformation.',
          ],
          source: 'Mehmet Ali Gülşen, Chief Digital Transformation Officer',
          cta: { label: 'Read more', href: '/en/customers/petrol-ofisi-group' },
        },
        {
          company: 'ebebek',
          logo: { name: 'ebebek', src: '/logo/ebebekcard.png' },
          portrait: { src: '/photo/portrait-baris-simsek.png', alt: 'Barış Şimşek' },
          quote: [
            'It allows us to clearly see where we were in the past, where we stand today, and '
            + 'where we need to go in the future by comparing ourselves with industry and '
            + 'global benchmarks.',
            'With periodic evaluations, we can ensure we are on the right track and take the '
            + 'necessary actions accordingly.',
          ],
          source: 'Barış Şimşek, CTO',
          cta: { label: 'Read more', href: '/en/customers/ebebek' },
        },
      ],
      cta: { label: 'See success stories', href: LINKS.successStories },
    },
  ],
}
